import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  FiInbox,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import Notification from "../../components/common/notification/Notification";
import Spinner from "../../components/common/spinner/Spinner";
import { usePropertyApiClient } from "./services/usePropertyApiClient";
import { createPropertyService } from "./services/propertyService";
import { PROPERTY_TABS, PROPERTY_DEFAULT_PAGE_SIZE } from "./constants/propertyEndpoints";
import PropertyPageHeader from "./components/PropertyPageHeader";
import PropertyFilterToolbar from "./components/PropertyFilterToolbar";
import PropertyCard from "./components/PropertyCard";
import PropertyTableView from "./components/PropertyTableView";
import PropertyDeleteModal from "./components/PropertyDeleteModal";
import "./PropertiesPage.css";

const PropertiesPage = ({ routeBase = "/customer/properties" }) => {
  const navigate = useNavigate();

  const authUser = useSelector((state) => state.auth?.user);
  const currentUserId =
    authUser?.user?.id || authUser?.id || authUser?.userDetails?.id || 1;
  const currentUserName =
    [authUser?.user?.first_name, authUser?.user?.last_name].filter(Boolean).join(" ") ||
    authUser?.user?.name ||
    "User";

  const userType =
    authUser?.user?.userType?.name ||
    authUser?.user?.userType ||
    authUser?.userType ||
    "CUSTOMER";
  const isPartner = String(userType).toUpperCase().includes("PARTNER");
  const isAdmin =
    String(userType).toUpperCase().includes("ADMIN") ||
    String(userType).toUpperCase().includes("ALL_HOME_DESIRE");

  const apiClient = usePropertyApiClient();
  const propertyService = useMemo(
    () => createPropertyService(apiClient),
    [apiClient]
  );

  // Active Tab
  const [activeTab, setActiveTab] = useState(
    isPartner ? PROPERTY_TABS.PREFERRED : PROPERTY_TABS.ALL
  );

  // List data state
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(null);

  // Filters state
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [selectedCity, setSelectedCity] = useState("");

  // Metadata for dropdowns
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  // View mode
  const [viewMode, setViewMode] = useState("grid");

  // Modals state
  const [propertyToDelete, setPropertyToDelete] = useState(null);

  // Load initial metadata
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const [typesData, statesData] = await Promise.all([
          propertyService.fetchPropertyTypes(),
          propertyService.fetchStates(1),
        ]);
        setPropertyTypes(typesData || []);
        setStates(statesData || []);
      } catch (err) {
        console.error("Failed to load metadata:", err);
      }
    };
    fetchMetadata();
  }, [propertyService]);

  // State selection handler
  const handleStateFilterChange = async (stateName) => {
    setSelectedState(stateName);
    setSelectedCity("");
    if (!stateName) {
      setCities([]);
      return;
    }
    const stObj = states.find((s) => s.name === stateName);
    if (stObj?.id) {
      try {
        const citiesData = await propertyService.fetchCities(stObj.id);
        setCities(citiesData || []);
      } catch (err) {
        console.error("Failed to load cities for state:", err);
      }
    }
  };

  // Main data fetching logic
  const fetchProperties = useCallback(async () => {
    try {
      setLoading(true);
      let data = null;

      if (activeTab === PROPERTY_TABS.MY) {
        data = await propertyService.listMy({
          user_id: currentUserId,
          page_number: currentPage,
          page_size: PROPERTY_DEFAULT_PAGE_SIZE,
          order_by: "DESC",
        });
      } else if (
        activeTab === PROPERTY_TABS.PREFERRED ||
        selectedType ||
        selectedState ||
        selectedCity ||
        searchTerm
      ) {
        data = await propertyService.listPreferred({
          property_type: selectedType,
          state: selectedState,
          city: selectedCity,
          keyboard_search: searchTerm,
          page_number: currentPage,
          page_size: PROPERTY_DEFAULT_PAGE_SIZE,
          order_by: "DESC",
          is_partner: true,
        });
      } else {
        data = await propertyService.listGeneral({
          page_number: currentPage,
          page_size: PROPERTY_DEFAULT_PAGE_SIZE,
          order_by: "DESC",
        });
      }

      let list =
        data?.properties ||
        data?.propertyAssets ||
        data?.data ||
        data?.leads ||
        (Array.isArray(data) ? data : []);

      // Client-side fallback filtering
      if (searchTerm || selectedType || selectedState || selectedCity) {
        list = list.filter((p) => {
          const title = p.project_name || p.property_name || p.title || "";
          const desc = p.property_description || p.description || "";
          const matchSearch =
            !searchTerm ||
            title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            desc.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (p.city_name && p.city_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (p.state_name && p.state_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (p.partner?.first_name && p.partner.first_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (p.customer?.first_name && p.customer.first_name.toLowerCase().includes(searchTerm.toLowerCase()));

          const propTypeName = typeof p.property_type === "object" ? p.property_type?.name : p.property_type;
          const matchType = !selectedType || propTypeName === selectedType;
          const matchState = !selectedState || p.state_name === selectedState;
          const matchCity = !selectedCity || p.city_name === selectedCity;

          return matchSearch && matchType && matchState && matchCity;
        });
      }

      setProperties(list);
      setTotalPages(data?.totalPages || data?.totalPage || data?.total_pages || 1);
      if (typeof data?.totalCount === "number") {
        setTotalCount(data.totalCount);
      } else if (typeof data?.totalRecord === "number") {
        setTotalCount(data.totalRecord);
      } else {
        setTotalCount(list.length);
      }
    } catch (err) {
      console.warn("Error fetching properties:", err);
      setError("Unable to load properties. Please check your network connection.");
      setProperties([]);
    } finally {
      setLoading(false);
    }
  }, [
    activeTab,
    currentPage,
    currentUserId,
    searchTerm,
    selectedCity,
    selectedState,
    selectedType,
    propertyService,
  ]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  // Tab change
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setCurrentPage(1);
  };

  // Reset filters
  const handleResetFilters = () => {
    setSearchTerm("");
    setSelectedType("");
    setSelectedState("");
    setSelectedCity("");
    setCurrentPage(1);
  };

  // Details & Discussion Full Page Navigation
  const handleViewDetails = (property) => {
    const cleanBase = routeBase.replace(/\/+$/, "");
    navigate(`${cleanBase}/${property.id}`);
  };

  const handleOpenComments = (property) => {
    const cleanBase = routeBase.replace(/\/+$/, "");
    navigate(`${cleanBase}/${property.id}/discussion`);
  };

  const handleDeletePrompt = (property) => {
    setPropertyToDelete(property);
  };

  const handlePropertyDeleted = (deletedId) => {
    setProperties((prev) => prev.filter((p) => p.id !== deletedId));
  };

  // Restrict creation: only Partners and Admins can create listings, never customers
  const canCreate = (isPartner || isAdmin) && !routeBase.startsWith("/customer");

  return (
    <div className="properties-module-page">
      <Notification />

      {/* Page Header */}
      <PropertyPageHeader
        title={routeBase.startsWith("/customer") ? "Browse Properties" : "Shared Properties"}
        description={
          routeBase.startsWith("/customer")
            ? "Explore verified residential apartments, villas, plots, and commercial properties across operating regions."
            : "Browse premium real estate, residential developments, plots, commercial listings, and exclusive property options."
        }
        totalCount={totalCount}
        canCreate={canCreate}
        createLink="/property/add-property"
      />

      {/* Filter Toolbar */}
      <PropertyFilterToolbar
        activeTab={activeTab}
        onTabChange={handleTabChange}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onSearchSubmit={fetchProperties}
        selectedType={selectedType}
        onTypeChange={(val) => {
          setSelectedType(val);
          setCurrentPage(1);
        }}
        propertyTypes={propertyTypes}
        selectedState={selectedState}
        onStateChange={handleStateFilterChange}
        states={states}
        selectedCity={selectedCity}
        onCityChange={(val) => {
          setSelectedCity(val);
          setCurrentPage(1);
        }}
        cities={cities}
        onResetFilters={handleResetFilters}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        showMyTab={Boolean(currentUserId)}
      />

      {/* Main Content Area */}
      {loading ? (
        <div className="properties-loading-wrap">
          <Spinner loading={true} />
          <p className="mt-3 text-muted">Loading properties...</p>
        </div>
      ) : error ? (
        <div className="alert alert-danger d-flex align-items-center justify-content-between p-3 rounded-3 my-4" role="alert">
          <span>{error}</span>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={fetchProperties}>
            Retry
          </button>
        </div>
      ) : properties.length === 0 ? (
        <div className="properties-empty-state">
          <div className="properties-empty-state__icon">
            <FiInbox size={48} />
          </div>
          <h3 className="properties-empty-state__title">
            {searchTerm || selectedType || selectedState || selectedCity
              ? "No Matching Properties"
              : isPartner && activeTab === PROPERTY_TABS.MY
              ? "No Property Listings Yet"
              : "No Properties Available"}
          </h3>
          <p className="properties-empty-state__text">
            {searchTerm || selectedType || selectedState || selectedCity
              ? "Try adjusting your search terms or filters."
              : isPartner && activeTab === PROPERTY_TABS.MY
              ? "Create your first property listing to start presenting your properties to customers."
              : "There are currently no active properties listed in this category. Check back soon for new listings."}
          </p>
          {(searchTerm || selectedType || selectedState || selectedCity) && (
            <button
              type="button"
              className="btn btn-outline-success mt-2"
              onClick={handleResetFilters}
            >
              Clear Filters
            </button>
          )}
          {!(searchTerm || selectedType || selectedState || selectedCity) && isPartner && activeTab === PROPERTY_TABS.MY && (
            <button
              type="button"
              className="btn btn-primary mt-2"
              onClick={() => navigate("/add-property")}
            >
              Create Listing
            </button>
          )}
        </div>
      ) : viewMode === "grid" ? (
        <div className="properties-grid">
          {properties.map((property) => {
            const isOwner =
              currentUserId &&
              Number(property.partner?.id || property.customer?.id || property.user?.id) ===
                Number(currentUserId);
            const canDelete = isOwner || isAdmin;

            return (
              <PropertyCard
                key={property.id}
                property={property}
                onViewDetails={handleViewDetails}
                onOpenComments={handleOpenComments}
                onDelete={handleDeletePrompt}
                canDelete={canDelete}
              />
            );
          })}
        </div>
      ) : (
        <PropertyTableView
          properties={properties}
          onViewDetails={handleViewDetails}
          onOpenComments={handleOpenComments}
          onDelete={handleDeletePrompt}
          canDelete={isAdmin || isPartner}
        />
      )}

      {/* Pagination Bar */}
      {totalPages > 1 && !loading && (
        <div className="properties-pagination-bar">
          <button
            type="button"
            className="properties-page-nav-btn"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          >
            <FiChevronLeft size={16} />
            <span>Previous</span>
          </button>

          <span className="properties-page-counter">
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          </span>

          <button
            type="button"
            className="properties-page-nav-btn"
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          >
            <span>Next</span>
            <FiChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <PropertyDeleteModal
        show={Boolean(propertyToDelete)}
        onHide={() => setPropertyToDelete(null)}
        property={propertyToDelete}
        propertyService={propertyService}
        onPropertyDeleted={handlePropertyDeleted}
      />
    </div>
  );
};

export default PropertiesPage;
